import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setStoredToken } from "@/utils/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, object = user, false = not authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from Google OAuth callback (session_id in URL fragment),
    // skip the /me check — AuthCallback will establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    if (data.session_token) setStoredToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (role, payload) => {
    const endpoint = {
      parent: "/api/auth/signup/parent",
      doctor: "/api/auth/signup/doctor",
      school: "/api/auth/signup/school",
      class_teacher: "/api/auth/signup/class-teacher",
    }[role];
    if (!endpoint) throw new Error("Invalid role");
    const { data } = await api.post(endpoint, payload);
    if (data.session_token) setStoredToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      // ignore
    }
    setStoredToken(null);
    setUser(false);
    window.location.href = "/";
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data.user);
      return data.user;
    } catch (e) {
      setUser(false);
      throw e;
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, refreshUser, formatApiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
